import {
  Button,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';

import {
  FRONT_WARNING,
  RIGHTS_IS_CREATE,
  RIGHTS_IS_DELETE,
  RIGHTS_IS_EDIT,
  RIGHTS_WORK_SCHEDULES,
} from '@app/constants';
import { useAccessRights, useWindowSize } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { correctTimeInterval } from '@app/utils';

import {
  createTimeIntervals,
  handlePeriodChange,
} from '@pages/WorkSchedule/utils';

import {
  INITIAL_ADDITION_FILTERS,
  INITIAL_END_WORKING_TIME,
  INITIAL_START_WORKING_TIME,
  WS_DAYS,
  WS_ONE_DAY,
  WS_SHIFTS,
  WS_WEEKS,
} from '@pages/WorkSchedule/constants/filters';

import {
  FullScreenButton,
  ResetFiltersButton,
} from '@pages/WorkSchedule/components';
import {
  days,
  defaultSelectedDays,
  periodOptions,
  scheduleModeOptions,
} from '@pages/WorkSchedule/data';
import type { NWorkSchedule } from '@pages/WorkSchedule/WorkSchedule.types';

import type { BaseOptionType } from 'antd/es/select';

const { Text } = Typography;

export const ScheduleHeader: NWorkSchedule.Header = ({
  filters: staticFilters,
  isFilterByDays,
  form,
  startTime: staticStartTime,
  endTime: staticEndTime,
  daysCount,
  spaceInDays,
  selectedDays,
  setSelectedDays,
  setSpaceInDays,
  setDaysCount,
  setFilterByDays,
  showDevNotification,
  setMode,
}) => {
  const { hasUserRight: deletionInPresentAndFeature } = useAccessRights(
    RIGHTS_WORK_SCHEDULES,
    RIGHTS_IS_DELETE
  );
  const { hasUserRight: editionInPresentAndFeature } = useAccessRights(
    RIGHTS_WORK_SCHEDULES,
    RIGHTS_IS_EDIT
  );
  const { hasUserRight: creationInPresentAndFeature } = useAccessRights(
    RIGHTS_WORK_SCHEDULES,
    RIGHTS_IS_CREATE
  );

  const options = useMemo(() => {
    let allowedOptions = [...scheduleModeOptions];

    if (!deletionInPresentAndFeature) {
      allowedOptions = allowedOptions.filter(({ value }) => value !== 'delete');
    }

    if (!editionInPresentAndFeature) {
      allowedOptions = allowedOptions.filter(({ value }) => value !== 'edit');
    }

    return allowedOptions;
  }, [deletionInPresentAndFeature, editionInPresentAndFeature]);

  const { showNotification } = useContext(NotificationContext);
  const [isFullScreenVersion, setFullScreenVersion] = useState(false);
  const { innerWidth } = useWindowSize();
  const [dynamicFilters, setDynamicFilters] = useState<NWorkSchedule.Filters>(
    INITIAL_ADDITION_FILTERS
  );
  const [startPeriod, setStartPeriod] = useState(INITIAL_START_WORKING_TIME);
  const [endPeriod, setEndPeriod] = useState(INITIAL_END_WORKING_TIME);

  useEffect(() => {
    correctTimeInterval({
      inputStart: startPeriod,
      inputEnd: endPeriod,
      cbToSaveBothValues: ([startPeriod, endPeriod]) => {
        if (staticStartTime) staticStartTime.current = startPeriod;
        setStartPeriod(startPeriod);
        form?.setFieldValue('startTime', startPeriod);

        if (staticEndTime) staticEndTime.current = endPeriod;
        setEndPeriod(endPeriod);
        form?.setFieldValue('endTime', endPeriod);
      },
      showNotification,
    });
  }, [
    startPeriod,
    endPeriod,
    staticStartTime,
    staticEndTime,
    form,
    showNotification,
  ]);

  const resetFilters = () => {
    if (!form)
      return showNotification('Форма фильтров не найдена', FRONT_WARNING);

    if (!staticStartTime)
      return showNotification(
        'Не удалось извлечь информацию по времени начала рабочего дня',
        FRONT_WARNING
      );

    if (!staticEndTime)
      return showNotification(
        'Не удалось извлечь информацию по времени окончания рабочего дня',
        FRONT_WARNING
      );

    setDynamicFilters(INITIAL_ADDITION_FILTERS);

    staticFilters.current = {
      period: {
        primaryPeriod: WS_DAYS,
        detailedPeriod: WS_ONE_DAY,
      },
    };

    staticStartTime.current = INITIAL_START_WORKING_TIME;
    staticEndTime.current = INITIAL_END_WORKING_TIME;

    setStartPeriod(INITIAL_START_WORKING_TIME);
    setEndPeriod(INITIAL_END_WORKING_TIME);
    setDaysCount && setDaysCount('2');
    setSpaceInDays && setSpaceInDays('2');

    setFilterByDays(true);
    setSelectedDays && setSelectedDays(defaultSelectedDays);

    form.resetFields();
    form.setFieldValue('startTime', INITIAL_START_WORKING_TIME.label);
    form.setFieldValue('endTime', INITIAL_END_WORKING_TIME.label);
  };

  const primaryPeriod = dynamicFilters?.period?.primaryPeriod || WS_DAYS;
  const detailedPeriod = dynamicFilters?.period?.detailedPeriod || WS_ONE_DAY;

  return (
    <Form
      form={form}
      className="adding-general-records-header"
      name="adding-general-records-header-form"
      layout="vertical"
      initialValues={{
        mode: 'edit',
      }}
    >
      <Row
        className="filters-and-actions"
        justify="space-between"
        align="bottom"
      >
        <Space size="large" className="filters-container">
          <Form.Item style={{ marginBottom: 0 }}>
            <Space className="filters-container_item">
              <Text className="filter-label" data-testid="period">
                Период:
              </Text>
              <Select
                className="custom-select period-select"
                size="small"
                style={{ width: '120px' }}
                value={periodOptions.find(
                  ({ value }) => value === detailedPeriod
                )}
                popupMatchSelectWidth={false}
                onSelect={showDevNotification}
                popupRender={() => (
                  <>
                    <Space
                      style={{ padding: '0 8px 4px' }}
                      className="period-select-popup"
                    >
                      <Radio.Group
                        value={primaryPeriod}
                        onChange={(event) =>
                          handlePeriodChange(
                            event,
                            staticFilters,
                            setDynamicFilters,
                            setFilterByDays
                          )
                        }
                      >
                        <Space direction="vertical">
                          <Radio
                            data-test-id={WS_DAYS}
                            value={WS_DAYS}
                            data-testid="for-days"
                          >
                            По дням
                          </Radio>
                          {isFilterByDays && (
                            <Select
                              options={periodOptions}
                              value={detailedPeriod}
                              className="week-select"
                              popupRender={(menu) => (
                                <div data-testid="weekday-dropdown">{menu}</div>
                              )}
                              optionRender={(option) => (
                                <span data-testid="weekday-item">
                                  {option.label}
                                </span>
                              )}
                              onChange={(
                                value: NWorkSchedule.Filters['period']['detailedPeriod']
                              ) => {
                                staticFilters.current = {
                                  period: {
                                    ...dynamicFilters.period,
                                    detailedPeriod: value,
                                  },
                                };

                                setDynamicFilters({
                                  period: {
                                    ...dynamicFilters.period,
                                    detailedPeriod: value,
                                  },
                                });
                              }}
                            />
                          )}
                          <Radio
                            data-test-id={WS_SHIFTS}
                            value={WS_SHIFTS}
                            data-testid="for-shifts"
                          >
                            Посменно
                          </Radio>
                          {primaryPeriod === WS_SHIFTS && (
                            <Space>
                              <Input
                                value={daysCount}
                                onChange={(event) => {
                                  if (setDaysCount) {
                                    setDaysCount(event.target.value);
                                  }
                                }}
                                style={{ width: '40px' }}
                                data-testid="initial-shift"
                              />
                              <Text>/</Text>
                              <Input
                                value={spaceInDays}
                                onChange={(event) => {
                                  if (setSpaceInDays) {
                                    setSpaceInDays(event.target.value);
                                  }
                                }}
                                style={{ width: '40px' }}
                                data-testid="repeat-shift"
                              />
                              <Select
                                options={periodOptions.filter(
                                  ({ value }) => value !== WS_ONE_DAY
                                )}
                                value={detailedPeriod}
                                popupMatchSelectWidth={false}
                                className="week-select"
                                popupRender={(menu) => (
                                  <div data-testid="weekday-dropdown">
                                    {menu}
                                  </div>
                                )}
                                optionRender={(option) => (
                                  <span data-testid="weekday-item">
                                    {option.label}
                                  </span>
                                )}
                                onChange={(
                                  value: NWorkSchedule.Filters['period']['detailedPeriod']
                                ) => {
                                  staticFilters.current = {
                                    period: {
                                      primaryPeriod: WS_SHIFTS,
                                      detailedPeriod: value,
                                    },
                                  };
                                  setDynamicFilters({
                                    period: {
                                      primaryPeriod: WS_SHIFTS,
                                      detailedPeriod: value,
                                    },
                                  });
                                }}
                              />
                            </Space>
                          )}
                          <Radio
                            data-test-id={WS_WEEKS}
                            value={WS_WEEKS}
                            data-testid="for-weekdays"
                          >
                            По дням недели
                          </Radio>
                          {primaryPeriod === WS_WEEKS && (
                            <Space direction="vertical">
                              <Select
                                options={periodOptions.filter(
                                  ({ value }) => value !== WS_ONE_DAY
                                )}
                                value={detailedPeriod}
                                popupMatchSelectWidth={false}
                                className="weekday-select"
                                popupRender={(menu) => (
                                  <div data-testid="weekday-dropdown">
                                    {menu}
                                  </div>
                                )}
                                optionRender={(option) => (
                                  <span data-testid="weekday-item">
                                    {option.label}
                                  </span>
                                )}
                                onChange={(
                                  value: NWorkSchedule.Filters['period']['detailedPeriod']
                                ) => {
                                  staticFilters.current = {
                                    period: {
                                      primaryPeriod: WS_WEEKS,
                                      detailedPeriod: value,
                                    },
                                  };
                                  setDynamicFilters({
                                    period: {
                                      primaryPeriod: WS_WEEKS,
                                      detailedPeriod: value,
                                    },
                                  });
                                }}
                              />
                              <Space size="small">
                                {days.map(({ title }) => {
                                  const dayHumanId = days.find(
                                    ({ title: _title }) => _title === title
                                  )?.humanId;
                                  let isSelectedDay = false;

                                  if (dayHumanId) {
                                    isSelectedDay =
                                      Object(selectedDays)[dayHumanId];
                                  }

                                  return (
                                    <Button
                                      key={title}
                                      size="small"
                                      type={
                                        isSelectedDay ? 'primary' : 'default'
                                      }
                                      onClick={() => {
                                        if (
                                          selectedDays &&
                                          setSelectedDays &&
                                          dayHumanId
                                        ) {
                                          setSelectedDays({
                                            ...selectedDays,
                                            [dayHumanId]: isSelectedDay
                                              ? false
                                              : true,
                                          });
                                        }
                                      }}
                                    >
                                      {title}
                                    </Button>
                                  );
                                })}
                              </Space>
                            </Space>
                          )}
                        </Space>
                      </Radio.Group>
                    </Space>
                  </>
                )}
              />
            </Space>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space className="filters-container_item">
              <Text className="filter-label" data-testid="work-time">
                Рабочее время:
              </Text>
              <Select
                className="time-select"
                size="small"
                style={{ width: '200px' }}
                value={`${startPeriod.label}-${endPeriod.label}`}
                popupMatchSelectWidth={false}
                optionRender={(option) => (
                  <span data-testid="time-from-item">{option.label}</span>
                )}
                popupRender={() => (
                  <>
                    <Space
                      style={{ padding: '0 8px 4px' }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      data-testid="time-from-dropdown"
                    >
                      <Form.Item name="startTime" style={{ marginBottom: 0 }}>
                        <Select
                          value={startPeriod}
                          className="time-from"
                          options={createTimeIntervals().map(
                            (item: string, index: number) => ({
                              value: index,
                              label: item,
                            })
                          )}
                          popupRender={(menu) => (
                            <div data-testid="time-from-dropdown">{menu}</div>
                          )}
                          onChange={(_, option: BaseOptionType) => {
                            if (!staticStartTime)
                              return showNotification(
                                'Не удалось извлечь информацию по времени начала рабочего дня',
                                FRONT_WARNING
                              );

                            staticStartTime.current = option;
                            setStartPeriod(option);
                          }}
                          size="small"
                          style={{
                            width: 80,
                          }}
                        />
                      </Form.Item>
                      <span>–</span>
                      <Form.Item name="endTime" style={{ marginBottom: 0 }}>
                        <Select
                          value={endPeriod}
                          className="time-to"
                          popupRender={(menu) => (
                            <div data-testid="time-to-dropdown">{menu}</div>
                          )}
                          options={createTimeIntervals().map(
                            (item: string, index: number) => ({
                              value: index,
                              label: item,
                            })
                          )}
                          onChange={(_, option: BaseOptionType) => {
                            if (!staticEndTime)
                              return showNotification(
                                'Не удалось извлечь информацию по времени окончания рабочего дня',
                                FRONT_WARNING
                              );

                            staticEndTime.current = option;
                            setEndPeriod(option);
                          }}
                          size="small"
                          style={{
                            width: 80,
                          }}
                        />
                      </Form.Item>
                    </Space>
                  </>
                )}
              />
            </Space>
          </Form.Item>
          {!deletionInPresentAndFeature &&
          !creationInPresentAndFeature &&
          !editionInPresentAndFeature ? null : (
            <Row className="filters-and-actions" justify="end" align="bottom">
              <Form.Item name="mode" className="mg-0">
                <Radio.Group
                  size="small"
                  options={options}
                  optionType="button"
                  className="action-type-radio-group"
                  onChange={({ target: { value } }) => {
                    if (value === 'delete' && !deletionInPresentAndFeature) {
                      showNotification(
                        'У вас нет прав на удаление!',
                        FRONT_WARNING
                      );
                    }

                    setMode(value);
                  }}
                />
              </Form.Item>
            </Row>
          )}
        </Space>

        {innerWidth <= 1185 ? (
          <ResetFiltersButton resetFilters={resetFilters} />
        ) : (
          <Space>
            <ResetFiltersButton resetFilters={resetFilters} />
            <FullScreenButton
              isFullScreenVersion={isFullScreenVersion}
              setFullScreenVersion={setFullScreenVersion}
            />
          </Space>
        )}
      </Row>
    </Form>
  );
};
